'use client';

import React, { useState, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { ChevronUp, Heart, X, ExternalLink, Copy, CheckCircle2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getPublicDonationSettings, type DonationSettings } from '@/lib/api-client';
import { useSite } from '@/context/SiteContext';

function ensureAbsoluteUrl(url: string): string {
  if (!url) return '#';
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('//')) return url;
  return `https://${url}`;
}

function CopyBtn({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={async () => { try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch {} }}
      className="p-1.5 text-fg-muted hover:text-white transition-colors"
    >
      {copied ? <CheckCircle2 size={14} className="text-emerald-400" /> : <Copy size={14} />}
    </button>
  );
}

export default function ScrollToTop() {
  const [visible, setVisible] = useState(false);
  const [scrolling, setScrolling] = useState(false);
  const [donateHidden, setDonateHidden] = useState(false);
  const [donationSettings, setDonationSettings] = useState<DonationSettings | null>(null);
  const [showPopup, setShowPopup] = useState(false);
  const { siteName } = useSite();
  const pathname = usePathname();
  const isDashboard = pathname === '/';
  const quickTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const donateTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const onScroll = () => {
      setVisible(window.scrollY > 400);
      // Hide all floating buttons while scrolling on mobile
      setScrolling(true);
      setDonateHidden(true);
      document.body.classList.add('mobile-scrolling');
      // Chat bubble + scroll-to-top: reappear after 2s
      if (quickTimer.current) clearTimeout(quickTimer.current);
      quickTimer.current = setTimeout(() => { setScrolling(false); document.body.classList.remove('mobile-scrolling'); }, 2000);
      // Donate: reappear after 10s
      if (donateTimer.current) clearTimeout(donateTimer.current);
      donateTimer.current = setTimeout(() => setDonateHidden(false), 10000);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { window.removeEventListener('scroll', onScroll); if (quickTimer.current) clearTimeout(quickTimer.current); if (donateTimer.current) clearTimeout(donateTimer.current); };
  }, []);

  useEffect(() => {
    getPublicDonationSettings()
      .then((d) => setDonationSettings(d))
      .catch(() => {});
  }, []);

  const donationsEnabled = donationSettings?.enabled ?? false;

  if (!visible) return null;

  const hasPayPal = !!donationSettings?.paypalLink;
  const hasZelle = !!donationSettings?.zelleEmail;
  const hasVenmo = !!donationSettings?.venmoHandle;

  return (
    <>
      {/* Scroll to top — above chat bubble, hidden while scrolling on mobile */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className={cn(
          'fixed bottom-[4.5rem] right-6 z-30 p-2.5 rounded-full shadow-lg transition-all bg-sky-900/50 hover:bg-sky-800/70 text-sky-300 backdrop-blur-sm lg:hidden',
          scrolling && 'opacity-0 pointer-events-none',
        )}
        aria-label="Scroll to top"
      >
        <ChevronUp size={18} />
      </button>

      {/* Donation heart — above scroll-to-top button */}
      {donationsEnabled && isDashboard && (
        <button
          onClick={() => setShowPopup(true)}
          className={cn(
            'fixed bottom-[7.5rem] right-6 z-30 p-2.5 rounded-full shadow-lg transition-all bg-rose-900/50 hover:bg-rose-800/70 text-rose-300 backdrop-blur-sm lg:hidden',
            donateHidden && 'opacity-0 pointer-events-none',
          )}
          aria-label="Support us"
        >
          <Heart size={18} />
        </button>
      )}

      {/* Donation popup */}
      {showPopup && donationSettings && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4" onClick={() => setShowPopup(false)}>
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div
            className="relative bg-card-bg border border-default rounded-2xl w-full max-w-sm max-h-[80vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close */}
            <button onClick={() => setShowPopup(false)} className="absolute top-3 right-3 p-1.5 text-fg-subtle hover:text-white transition-colors z-10">
              <X size={18} />
            </button>

            <div className="p-5">
              {/* Header */}
              <div className="text-center mb-5">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-rose-500/20 to-pink-500/10 border border-rose-500/20 flex items-center justify-center mx-auto mb-3">
                  <Heart size={22} className="text-rose-400" />
                </div>
                <h2 className="text-lg font-bold text-white mb-1.5">Support {siteName}</h2>
                <p className="text-xs text-fg-muted leading-relaxed">
                  {`Help us keep ${siteName} free for everyone. Any contribution helps cover server costs.`}
                </p>
              </div>

              {/* Custom message */}
              {donationSettings.message && (
                <div className="bg-surface border border-default rounded-lg px-4 py-3 mb-4 text-xs text-slate-300 leading-relaxed">
                  {donationSettings.message}
                </div>
              )}

              {/* Payment methods */}
              <div className="space-y-2.5">
                {hasPayPal && (
                  <a
                    href={ensureAbsoluteUrl(donationSettings.paypalLink)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 bg-surface border border-default rounded-lg px-4 py-3 hover:border-sky-500/30 transition-colors group"
                  >
                    <div className="w-8 h-8 rounded-lg bg-[#003087]/20 border border-[#003087]/30 flex items-center justify-center flex-shrink-0">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="text-[#009cde]">
                        <path d="M7.144 19.532l1.049-5.751c.11-.606.645-1.043 1.26-1.043h1.417c3.915 0 6.73-1.733 7.595-4.805.089-.32.159-.638.213-.954C18.28 6.98 18 7 17.697 7H9.001c-.614 0-1.149.437-1.26 1.043L5.002 19.532H7.144z" fill="currentColor" opacity=".5"/>
                        <path d="M18.677 6.979c-.054.316-.124.634-.213.954-.865 3.072-3.68 4.805-7.595 4.805H9.452c-.614 0-1.15.437-1.26 1.043l-1.05 5.751H5.003l1.739-9.51A1.282 1.282 0 018 9h9.697c.303 0 .583-.02.98.02z" fill="currentColor"/>
                      </svg>
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-white">PayPal</div>
                    </div>
                    <ExternalLink size={14} className="text-fg-subtle group-hover:text-sky-400" />
                  </a>
                )}

                {hasZelle && (
                  <div className="flex items-center gap-3 bg-surface border border-default rounded-lg px-4 py-3">
                    <div className="w-8 h-8 rounded-lg bg-violet-500/15 border border-violet-500/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-bold text-violet-400">Z</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-white">Zelle</div>
                      <div className="text-[11px] font-mono text-fg-muted truncate">{donationSettings.zelleEmail}</div>
                    </div>
                    <CopyBtn text={donationSettings.zelleEmail} />
                  </div>
                )}

                {hasVenmo && (
                  <div className="flex items-center gap-3 bg-surface border border-default rounded-lg px-4 py-3">
                    <div className="w-8 h-8 rounded-lg bg-sky-500/15 border border-sky-500/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-bold text-sky-400">V</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-white">Venmo</div>
                      <div className="text-[11px] font-mono text-fg-muted truncate">{donationSettings.venmoHandle}</div>
                    </div>
                    <CopyBtn text={donationSettings.venmoHandle} />
                  </div>
                )}

                {!hasPayPal && !hasZelle && !hasVenmo && (
                  <p className="text-xs text-fg-subtle text-center py-2">No payment methods configured yet.</p>
                )}
              </div>

              {/* Footer */}
              <p className="text-[10px] text-slate-600 text-center mt-4">Thank you for your support</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
